import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
    fetchingUserSubscriptionSelector,
    plansSelector,
    publishableKeySelector,
    subscribingSelector,
    updatingCreatorSubscriptionSelector,
    userPaymentMethodsSelector,
    userSubscriptionSelector,
} from '../EsSettings/selector';
import { effectiveOperatingCountryCodeSelector } from '../../selectors/selectors';
import { AccountSubscriptions as AccountSubscriptionsComponent } from '../../components/AccountSettings';
import {
    activateCreatorSubscription,
    cancelSubsciption,
    getSubscriptionPlans,
    initializeFlutterwaveSubscription,
    initializePaystackSubscription,
    setSubscription,
    subscribe,
    updateSubscription,
    verifyFlutterwaveSubscription,
    verifyPaystackSubscription,
} from '../../redux/subscription/actions';
import {
    getPaymentMethods,
    getPaymentRail,
    getPublishableKey,
    verifyFlutterwaveBillingCard,
} from '../../redux/payment/action';
import { IChangePayload } from '../../redux/subscription/types';
import { ISettingsProps } from '../../components/AccountSettings/AccountSubscriptions';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { toggleAuthModal } from '../../redux/authentication/action';
import { useAuth } from '../../module/AuthContext';
import {
    getCreatorSubscriptionProvider,
    parseUpdateCreatorSubscriptionPaymentResult,
    enrichCreatorSubscriptionFromCatalog,
} from '../../utils/creatorSubscriptionApi';
import { formatCreatorTrialActiveToast } from '../../utils/creatorSubscriptionTrial';
import { isFlutterwaveAbandonedReturn } from '../../utils/flutterwaveReturn';
import { consumeCreatorSubscribeResume, isFlutterwaveBillingCardTxRef } from '../../utils/creatorSubscribeResume';
import { CREATOR_SUBSCRIPTION_MANAGE_COPY } from '../../constants/creatorSubscriptionManageCopy';
import { CreatorUpdatePaymentModal } from '../../components/AccountSettings/CreatorUpdatePaymentModal';
import { IPaymentCard } from '../../types/global/generic.types';
import { defaultCheckoutProvider, resolvePaymentRail } from '../../utils/paymentRail';
import type { WalletFundProvider } from '../../redux/buzzbridge/types';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { PLAN_PAYMENT_MODAL_Z_INDEX } from '../../constants/subscriptionModalZIndex';

interface IAccountSubscription extends ISettingsProps {
    onDone?: VoidFunction;
    onSubscribeStart?: VoidFunction;
    paystackCallbackUrl?: string;
}

type HostedReturnVerifyKind = 'billing_card' | 'subscription';

function resolveHostedReturnVerify(
    searchParams: URLSearchParams,
): { kind: HostedReturnVerifyKind; message: string } | null {
    if (isFlutterwaveAbandonedReturn(searchParams)) return null;
    const transactionId = searchParams.get('transaction_id');
    const txRef = searchParams.get('tx_ref');
    const reference = searchParams.get('reference') ?? searchParams.get('trxref');
    if ((transactionId || txRef) && isFlutterwaveBillingCardTxRef(txRef ?? reference)) {
        return { kind: 'billing_card', message: 'Saving your card…' };
    }
    if (transactionId || txRef || reference) {
        return { kind: 'subscription', message: 'Confirming your subscription…' };
    }
    return null;
}

const AccountSubscriptions: React.FC<IAccountSubscription> = ({
    onDone,
    onSubscribeStart,
    onPlanSelect,
    onPlanDeselect,
    isPromptMode,
    paystackCallbackUrl,
    requireAnyEntitlement,
    requireAllEntitlements,
    allowedPlanClasses,
}) => {
    const { isSignedIn } = useAuth();
    const plans = useSelector(plansSelector);
    const paymentMethods = useSelector(userPaymentMethodsSelector);
    const subscription = useSelector(userSubscriptionSelector);
    const loading = useSelector(subscribingSelector);
    const fetchingSubscription = useSelector(fetchingUserSubscriptionSelector);
    const updatingPayment = useSelector(updatingCreatorSubscriptionSelector);
    const { publishableKey, isNonUs } = useSelector(publishableKeySelector);
    const countryCode: string = useSelector(effectiveOperatingCountryCodeSelector) ?? '';
    const [checkoutProvider, setCheckoutProvider] = useState<WalletFundProvider>(
        countryCode.toUpperCase() === 'NG' ? 'paystack' : 'stripe',
    );
    const isHostedCheckout = checkoutProvider === 'paystack' || checkoutProvider === 'flutterwave';

    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const [cancelling, setCancelling] = useState(false);
    const [reactivating, setReactivating] = useState(false);
    const [stripePaymentModalOpen, setStripePaymentModalOpen] = useState(false);
    const [hostedUpdateRedirecting, setHostedUpdateRedirecting] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [resumePlanCode, setResumePlanCode] = useState<string | null>(null);
    const [resumeSelectNewestCard, setResumeSelectNewestCard] = useState(false);
    const [verifyingReturnMessage, setVerifyingReturnMessage] = useState<string | null>(() => {
        const pending = resolveHostedReturnVerify(searchParams);
        return pending?.message ?? null;
    });

    useEffect(() => {
        const pending = resolveHostedReturnVerify(searchParams);
        const transactionId = searchParams.get('transaction_id');
        const txRef = searchParams.get('tx_ref');
        const reference = searchParams.get('reference') ?? searchParams.get('trxref');

        if (isFlutterwaveAbandonedReturn(searchParams)) {
            setVerifyingReturnMessage(null);
            setSearchParams({}, { replace: true });
            return;
        }

        if (!pending) {
            setVerifyingReturnMessage(null);
            return;
        }

        setVerifyingReturnMessage(pending.message);

        // Add-card from plan modal returns here with BILLCARD-* — save card only, reopen modal.
        if (pending.kind === 'billing_card') {
            dispatchPromise(
                verifyFlutterwaveBillingCard({
                    ...(transactionId ? { transaction_id: transactionId } : {}),
                    ...(txRef ? { tx_ref: txRef } : {}),
                }),
            )
                .then(() => {
                    setSearchParams({}, { replace: true });
                    showToastMessage('Card added', 'success');
                    dispatch(getPaymentMethods());
                    const resume = consumeCreatorSubscribeResume();
                    if (resume?.planCode) {
                        setResumePlanCode(resume.planCode);
                        setResumeSelectNewestCard(Boolean(resume.preferNewestCard));
                    }
                })
                .catch((err: any) => {
                    setSearchParams({}, { replace: true });
                    const message = err?.data?.message ?? err?.message ?? 'Could not verify card. Please try again.';
                    showToastMessage(message, 'error');
                    const resume = consumeCreatorSubscribeResume();
                    if (resume?.planCode) {
                        setResumePlanCode(resume.planCode);
                        setResumeSelectNewestCard(Boolean(resume.preferNewestCard));
                    }
                })
                .finally(() => {
                    setVerifyingReturnMessage(null);
                });
            return;
        }

        if (transactionId || txRef) {
            dispatchPromise(
                verifyFlutterwaveSubscription({
                    transaction_id: transactionId ?? undefined,
                    tx_ref: txRef ?? reference ?? undefined,
                    reference: txRef ?? reference ?? undefined,
                }),
            )
                .then((raw: any) => {
                    setSearchParams({}, { replace: true });
                    const payload = raw?.data ?? raw;
                    const verified = payload?.verified !== false;
                    const subscription = payload?.subscription ?? (verified ? payload : null);
                    if (!verified || !subscription) {
                        showToastMessage(
                            payload?.flutterwave_status
                                ? `Payment not completed (${payload.flutterwave_status}).`
                                : 'Payment not completed.',
                            'info',
                        );
                        return;
                    }
                    const updatedCard = Boolean(payload?.updated_card);
                    const isTrial = Boolean(
                        payload?.is_trial ||
                            subscription?.status === 'trialing' ||
                            subscription?.subscription_details?.is_trial,
                    );
                    const trialDays =
                        payload?.trial_days ??
                        subscription?.subscription_details?.trial_days ??
                        subscription?.trial?.days;
                    showToastMessage(
                        updatedCard
                            ? 'Card updated on Flutterwave'
                            : isTrial
                            ? formatCreatorTrialActiveToast(trialDays)
                            : 'Subscription activated!',
                        'success',
                    );
                    dispatch(setSubscription());
                })
                .catch((err: any) => {
                    const message =
                        err?.data?.message ?? err?.message ?? 'Could not verify subscription. Please contact support.';
                    showToastMessage(message, 'error');
                })
                .finally(() => {
                    setVerifyingReturnMessage(null);
                });
            return;
        }

        if (!reference) {
            setVerifyingReturnMessage(null);
            return;
        }

        dispatchPromise(verifyPaystackSubscription({ reference }))
            .then((sub: any) => {
                setSearchParams({}, { replace: true });
                const isTrial = Boolean(sub?.status === 'trialing' || sub?.subscription_details?.is_trial);
                const trialDays = sub?.subscription_details?.trial_days ?? sub?.trial?.days ?? sub?.trial_days;
                showToastMessage(
                    isTrial ? formatCreatorTrialActiveToast(trialDays) : 'Subscription activated!',
                    'success',
                );
                dispatch(setSubscription());
            })
            .catch((err: any) => {
                const message =
                    err?.data?.message ?? err?.message ?? 'Could not verify subscription. Please contact support.';
                showToastMessage(message, 'error');
            })
            .finally(() => {
                setVerifyingReturnMessage(null);
            });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refetchPlan = useCallback(() => {
        dispatch(setSubscription());
    }, [dispatch]);

    const handleCancel = async () => {
        if (!subscription?._id) return;
        setCancelling(true);
        try {
            await dispatchPromise(cancelSubsciption(subscription._id));
            const atRisk = subscription.premium_campaign_impact?.campaigns?.length ?? 0;
            showToastMessage(
                atRisk > 0
                    ? `Subscription cancelled. You'll keep premium campaign access until your billing period ends (${atRisk} campaign${
                          atRisk === 1 ? '' : 's'
                      }).`
                    : 'Your subscription has been cancelled.',
                'success',
            );
            refetchPlan();
        } catch {
            showToastMessage('Failed to cancel subscription. Please try again.', 'error');
        } finally {
            setCancelling(false);
        }
    };

    const handleReactivate = async () => {
        if (!subscription?._id) return;
        setReactivating(true);
        try {
            await dispatchPromise(activateCreatorSubscription(subscription._id));
            showToastMessage(CREATOR_SUBSCRIPTION_MANAGE_COPY.reactivateSuccess, 'success');
            refetchPlan();
        } catch (err: any) {
            const code = err?.code ?? err?.data?.code;
            const planCode =
                err?.data?.plan_code ?? subscription.subscription_plan ?? subscription.plan?.plan_code ?? null;

            if (code === 'reactivation_unavailable' && planCode && isHostedCheckout) {
                showToastMessage(CREATOR_SUBSCRIPTION_MANAGE_COPY.reactivateUnavailable, 'info');
                refetchPlan();
                try {
                    const initAction =
                        checkoutProvider === 'flutterwave'
                            ? initializeFlutterwaveSubscription
                            : initializePaystackSubscription;
                    const res = (await dispatchPromise(
                        initAction({
                            plan: planCode,
                            ...(paystackCallbackUrl ? { callback_url: paystackCallbackUrl } : {}),
                        }),
                    )) as any;
                    const payload = res?.data ?? res;
                    const authUrl = payload?.authorization_url;
                    if (authUrl) {
                        window.location.href = authUrl;
                        return;
                    }
                    showToastMessage(CREATOR_SUBSCRIPTION_MANAGE_COPY.resubscribeRedirectFail, 'error');
                } catch {
                    showToastMessage(CREATOR_SUBSCRIPTION_MANAGE_COPY.resubscribeRedirectFail, 'error');
                }
                return;
            }

            showToastMessage(err?.message ?? CREATOR_SUBSCRIPTION_MANAGE_COPY.reactivateFail, 'error');
            refetchPlan();
        } finally {
            setReactivating(false);
        }
    };

    const handleUpdatePayment = useCallback(async () => {
        if (!subscription?._id) return;
        const provider = getCreatorSubscriptionProvider(subscription);
        if (provider === 'paystack' || provider === 'flutterwave' || provider === 'stripe') {
            dispatch(getPaymentMethods());
            setStripePaymentModalOpen(true);
            return;
        }
        showToastMessage('No billing provider linked to this subscription.', 'info');
    }, [dispatch, subscription]);

    const handleHostedPaymentUpdateRedirect = useCallback(async () => {
        if (!subscription?._id) return;
        const provider = getCreatorSubscriptionProvider(subscription);
        if (provider !== 'paystack' && provider !== 'flutterwave') return;
        setHostedUpdateRedirecting(true);
        try {
            const raw = await dispatchPromise(
                updateSubscription({
                    subscriptionId: subscription._id,
                    callback_url: `${window.location.origin}${window.location.pathname}`,
                }),
            );
            const result = parseUpdateCreatorSubscriptionPaymentResult(raw);
            if (
                (result?.provider === 'paystack' || result?.provider === 'flutterwave') &&
                result.action === 'redirect' &&
                result.manage_payment_url
            ) {
                window.location.href = result.manage_payment_url;
                return;
            }
            showToastMessage(
                provider === 'flutterwave'
                    ? 'Could not open Flutterwave billing page.'
                    : 'Could not open Paystack billing page.',
                'error',
            );
        } catch {
            showToastMessage(
                provider === 'flutterwave'
                    ? 'Could not open Flutterwave billing page.'
                    : 'Could not open Paystack billing page.',
                'error',
            );
        } finally {
            setHostedUpdateRedirecting(false);
        }
    }, [dispatchPromise, subscription]);

    const handlePaymentConfirm = async (card: IPaymentCard) => {
        if (!subscription?._id) return;
        const provider = getCreatorSubscriptionProvider(subscription);

        if (provider === 'stripe') {
            if (!card.payment_method) return;
            try {
                const raw = await dispatchPromise(
                    updateSubscription({
                        subscriptionId: subscription._id,
                        paymentMethodId: card.payment_method,
                    }),
                );
                const result = parseUpdateCreatorSubscriptionPaymentResult(raw);
                if (result?.provider === 'stripe' && result.action === 'updated') {
                    showToastMessage('Payment method updated', 'success');
                    setStripePaymentModalOpen(false);
                    refetchPlan();
                }
            } catch {
                showToastMessage('Failed to update payment method. Please try again.', 'error');
            }
            return;
        }

        if (provider === 'paystack' || provider === 'flutterwave') {
            if (!card._id) return;
            try {
                const raw = await dispatchPromise(
                    updateSubscription({
                        subscriptionId: subscription._id,
                        payment_method_id: card._id,
                        paymentMethodId: card._id,
                    }),
                );
                const result = parseUpdateCreatorSubscriptionPaymentResult(raw);
                if (
                    (result?.provider === 'paystack' || result?.provider === 'flutterwave') &&
                    result.action === 'updated'
                ) {
                    showToastMessage('Payment method updated', 'success');
                    setStripePaymentModalOpen(false);
                    refetchPlan();
                    dispatch(getPaymentMethods());
                    return;
                }
                showToastMessage('Could not update payment method. Try again.', 'error');
            } catch {
                showToastMessage('Failed to update payment method. Please try again.', 'error');
            }
        }
    };

    const enrichedSubscription = useMemo(
        () => enrichCreatorSubscriptionFromCatalog(subscription, plans),
        [subscription, plans],
    );

    const handleSubscribe = async (payload: IChangePayload) => {
        if (!payload.paymentMethodId) {
            showToastMessage('Missing params', 'info');
            return;
        }
        onSubscribeStart?.();
        try {
            await dispatchPromise(subscribe(payload));
            onDone && onDone();
        } catch {
            showToastMessage('Subscription failed. Please try again.', 'error');
        }
    };

    const getSubscriptions = useCallback(() => {
        dispatchPromise(getPaymentRail({ category: 'subscriptions' }))
            .then((railData: unknown) => {
                const rail = resolvePaymentRail(railData as any);
                const provider = defaultCheckoutProvider(rail);
                setCheckoutProvider(provider);
                const keySource =
                    provider === 'paystack' ? 'paystack' : provider === 'flutterwave' ? 'flutterwave' : 'stripe';
                dispatch(getPublishableKey({ source: keySource }));
            })
            .catch(() => {
                const fallback = countryCode.toUpperCase() === 'NG' ? 'paystack' : 'stripe';
                setCheckoutProvider(fallback);
                dispatch(getPublishableKey({ source: fallback === 'paystack' ? 'paystack' : 'stripe' }));
            });
        dispatch(getSubscriptionPlans());
        if (!isPromptMode) {
            dispatch(getPaymentMethods());
            dispatch(setSubscription());
        }
    }, [dispatch, dispatchPromise, countryCode, isPromptMode]);

    useEffect(() => {
        getSubscriptions();
    }, [getSubscriptions]);

    // Keep Redux publishableKey aligned with checkout rail (prevents Stripe key leaking into Paystack popup).
    useEffect(() => {
        if (checkoutProvider === 'paystack') {
            dispatch(getPublishableKey({ source: 'paystack' }));
        } else if (checkoutProvider === 'flutterwave') {
            dispatch(getPublishableKey({ source: 'flutterwave' }));
        }
    }, [checkoutProvider, dispatch]);

    return (
        <>
            {verifyingReturnMessage ? (
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: PLAN_PAYMENT_MODAL_Z_INDEX + 50,
                        bgcolor: 'rgba(0,0,0,0.85)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        px: 3,
                    }}>
                    <CircularProgress sx={{ color: '#ffc965' }} size={48} />
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 18,
                            color: '#ffffff',
                            textAlign: 'center',
                        }}>
                        {verifyingReturnMessage}
                    </Typography>
                </Box>
            ) : null}
            <AccountSubscriptionsComponent
                onChange={payload => handleSubscribe(payload)}
                onCancel={handleCancel}
                onUpdatePayment={handleUpdatePayment}
                onReactivate={handleReactivate}
                cancelling={cancelling}
                reactivating={reactivating}
                updatingPayment={updatingPayment}
                fetchingSubscription={fetchingSubscription}
                plans={plans}
                userCards={paymentMethods}
                initialPlan={enrichedSubscription}
                isPromptMode={isPromptMode}
                loading={loading}
                paystackPublicKey={isHostedCheckout ? publishableKey : undefined}
                paystackCallbackUrl={paystackCallbackUrl}
                isFlutterwaveRail={checkoutProvider === 'flutterwave'}
                resumePlanCode={resumePlanCode}
                resumeSelectNewestCard={resumeSelectNewestCard}
                onResumePlanConsumed={() => setResumePlanCode(null)}
                onResumeSelectNewestConsumed={() => setResumeSelectNewestCard(false)}
                onHostedSubscribeSuccess={() => {
                    dispatch(setSubscription());
                    dispatch(getPaymentMethods());
                    onDone?.();
                }}
                onPlanSelect={() => {
                    // Lazy-fetch payment methods + current plan the first time a plan
                    // card is opened in prompt mode (modal context), instead of on mount.
                    if (isPromptMode) {
                        dispatch(getPaymentMethods());
                        dispatch(setSubscription());
                    }
                    onPlanSelect?.();
                }}
                onPlanDeselect={onPlanDeselect}
                onAuthRequired={!isSignedIn ? () => dispatch(toggleAuthModal(true)) : undefined}
                requireAnyEntitlement={requireAnyEntitlement}
                requireAllEntitlements={requireAllEntitlements}
                allowedPlanClasses={allowedPlanClasses}
            />
            <CreatorUpdatePaymentModal
                open={stripePaymentModalOpen}
                onClose={() => setStripePaymentModalOpen(false)}
                userCards={paymentMethods}
                provider={
                    (getCreatorSubscriptionProvider(subscription) as 'stripe' | 'paystack' | 'flutterwave') ||
                    (isNonUs ? 'paystack' : 'stripe')
                }
                loading={updatingPayment}
                hostedUpdating={hostedUpdateRedirecting}
                onHostedUpdate={
                    getCreatorSubscriptionProvider(subscription) === 'paystack' ||
                    getCreatorSubscriptionProvider(subscription) === 'flutterwave'
                        ? () => void handleHostedPaymentUpdateRedirect()
                        : undefined
                }
                onConfirm={handlePaymentConfirm}
            />
        </>
    );
};

export { AccountSubscriptions };
